import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSiteContent } from "../i18n/SiteContent";
import { ArrowRight } from "@phosphor-icons/react";

/**
 * Scroll-pinned horizontal journey.
 * On desktop: the section is `(N + 1) * 100vh` tall. A sticky inner viewport pins
 * to the top and translates a horizontal track based on the user's scroll
 * progress within the section. Each milestone occupies ~100vh of vertical scroll.
 *
 * Uses OpSys brand palette (#0F2A6E / #1B6FE0) with white background — matches
 * site guidelines while adopting the horizontal-scroll motion pattern.
 *
 * On mobile (<lg): falls back to a natural vertical stack so behaviour stays
 * touch-friendly.
 */
export default function StoryTimeline() {
  const { pg } = useSiteContent();

  const defaultPayload = useMemo(() => ({
    eyebrow: "Our Story",
    heading: "Two decades of quiet, compounding growth",
    sub: "From a specialist trading house in the UAE to a full-stack digital and engineering partner across seven country offices.",
    milestones: [
      { year: "2006", title: "Foundation",           desc: "OpSys established in the UAE — focused on infrastructure cabling and power backup for enterprise projects." },
      { year: "2014", title: "Regional Expansion",   desc: "Operations extended across KSA, Bahrain and Iraq — anchored on MEP contractors, system integrators and consultants." },
      { year: "2019", title: "Brand Portfolio",      desc: "Curated global-brand partnerships across UPS, BMS, biometric security, lighting control and structured cabling." },
      { year: "2022", title: "Africa Foothold",      desc: "Zambia JV launched; expansion into Kenya, Namibia, Botswana, Malawi and Zimbabwe for mining and solar." },
      { year: "2024", title: "OpSys Digital",        desc: "Digital arm formed — bringing IT consulting, AI automation, ELV/ICT and datacenter solutions under one roof." },
      { year: "2026", title: "AI & Sustainability",  desc: "Launching solar, hybrid microgrid and AI-led workflow automation products across seven country offices." },
    ],
  }), []);

  const rawStr = pg("story.timeline", null);
  const payload = useMemo(() => {
    if (rawStr && typeof rawStr === "string") {
      try {
        const parsed = JSON.parse(rawStr);
        if (parsed && parsed.milestones) return parsed;
      } catch { /* fall through */ }
    }
    if (rawStr && typeof rawStr === "object" && rawStr.milestones) return rawStr;
    return defaultPayload;
  }, [rawStr, defaultPayload]);

  const milestones = payload.milestones || defaultPayload.milestones;
  const total = milestones.length;

  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 across the whole section
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );

  // Track viewport width — switch to vertical mode on mobile
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Scroll listener with rAF throttle
  useEffect(() => {
    if (!isDesktop) return;
    let rafId = 0;
    const HEADER = 78; // sticky site header
    const compute = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = el.offsetHeight - (window.innerHeight - HEADER);
      // How far into the section have we scrolled?
      const scrolled = -rect.top + HEADER;
      const p = Math.max(0, Math.min(1, scrolled / Math.max(1, scrollable)));
      setProgress(p);
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { compute(); rafId = 0; });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", compute);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isDesktop]);

  // progress → active milestone index and per-track offset
  const idx = Math.min(total - 1, Math.floor(progress * total));
  const trackTranslatePct = progress * ((total - 1) / total) * 100;
  const progressPct = total > 1 ? (idx / (total - 1)) * 100 : 0;

  return (
    <section
      ref={sectionRef}
      id="story"
      data-testid="story-timeline"
      className="relative bg-white"
      style={{
        // Desktop: 1 viewport of pin + N viewports of horizontal scroll.
        // Mobile: natural height with vertical stack.
        height: isDesktop ? `calc(${total} * (100vh - 78px) + 100vh)` : "auto",
      }}
    >
      {/* ============ DESKTOP: sticky pinned journey ============ */}
      {isDesktop && (
        <div
          className="sticky top-[78px] overflow-hidden"
          style={{ height: "calc(100vh - 78px)" }}
          data-testid="story-pinned"
        >
          {/* Header — stays throughout the journey */}
          <div className="absolute top-0 left-0 right-0 pt-10 md:pt-14 px-8 lg:px-16 z-20 pointer-events-none">
            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold mb-3"
                style={{ color: "#1B6FE0" }}
                data-testid="story-timeline-eyebrow"
              >
                <span className="w-8 h-px" style={{ background: "#1B6FE0" }} />
                {payload.eyebrow}
              </div>
              <h2
                className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-[42px] leading-[1.05]"
                style={{ letterSpacing: "-0.028em", color: "#0F2A6E" }}
                data-testid="story-timeline-heading"
              >
                {payload.heading}
              </h2>
            </div>
          </div>

          {/* Horizontal track */}
          <div
            className="flex h-full will-change-transform"
            style={{
              width: `${total * 100}%`,
              transform: `translate3d(-${trackTranslatePct}%, 0, 0)`,
              transition: "transform 40ms linear",
            }}
            data-testid="story-track"
          >
            {milestones.map((m, i) => {
              const isActive = i === idx;
              // Per-panel local progress (0..1) — used for micro-parallax
              const local = Math.max(0, Math.min(1, (progress * total) - i));
              return (
                <article
                  key={i}
                  data-testid={`story-panel-${i}`}
                  className="relative flex items-center"
                  style={{ width: `${100 / total}%`, flex: `0 0 ${100 / total}%` }}
                >
                  <div className="w-full h-full grid grid-cols-2 items-center px-16 lg:px-24">
                    {/* LEFT — big ghost year + chapter counter */}
                    <div className="relative">
                      <div className="text-[11.5px] uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: "#1B6FE0" }}>
                        Chapter {String(i + 1).padStart(2, "0")} · {String(total).padStart(2, "0")}
                      </div>
                      <div
                        className="font-heading font-black leading-[0.85] tracking-tight"
                        style={{
                          fontSize: "clamp(180px, 20vw, 300px)",
                          color: "#0F2A6E",
                          letterSpacing: "-0.06em",
                          opacity: 0.08,
                          transform: `translateY(${(1 - local) * 30}px)`,
                          transition: "opacity 400ms ease",
                        }}
                      >
                        {m.year}
                      </div>
                      <div
                        className="absolute top-0 left-0 mt-[6.2rem] font-heading font-bold"
                        style={{
                          fontSize: "clamp(64px, 6.5vw, 92px)",
                          color: "#0F2A6E",
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                          opacity: isActive ? 1 : 0.35,
                          transform: `translateX(${(1 - local) * 30}px)`,
                          transition: "opacity 500ms ease, transform 500ms ease",
                        }}
                        data-testid={`story-year-display-${i}`}
                      >
                        {m.year}
                      </div>
                    </div>

                    {/* RIGHT — title + description */}
                    <div className="relative pl-8">
                      <div
                        className="max-w-xl"
                        style={{
                          opacity: isActive ? 1 : 0.35,
                          transform: `translateY(${(1 - local) * 24}px)`,
                          transition: "opacity 500ms ease, transform 500ms ease",
                        }}
                      >
                        <h3
                          className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-[46px] leading-[1.05]"
                          style={{ color: "#0F2A6E", letterSpacing: "-0.028em" }}
                          data-testid={isActive ? "story-active-title" : `story-title-${i}`}
                        >
                          {m.title}
                        </h3>
                        <p
                          className="mt-5 text-[15px] md:text-[17px] leading-[1.75] font-light"
                          style={{ color: "#1C2B3A" }}
                          data-testid={isActive ? "story-active-desc" : undefined}
                        >
                          {m.desc}
                        </p>
                        <div
                          className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.15em]"
                          style={{ color: "#1B6FE0" }}
                        >
                          {isActive && i < total - 1 && (
                            <>
                              Keep scrolling <ArrowRight size={14} weight="bold" />
                            </>
                          )}
                          {isActive && i === total - 1 && (
                            <>
                              End of journey <ArrowRight size={14} weight="bold" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* BOTTOM RAIL — sticky progress with year markers */}
          <div className="absolute left-0 right-0 bottom-8 md:bottom-10 px-8 lg:px-16 z-20">
            <div className="relative">
              <div className="h-px w-full" style={{ background: "rgba(15,42,110,0.12)" }} />
              <div
                className="absolute left-0 top-0 h-px"
                style={{
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg, #0F2A6E 0%, #1B6FE0 100%)",
                  transition: "width 40ms linear",
                }}
                data-testid="story-progress-fill"
              />

              {/* Year markers */}
              <div className="relative">
                {milestones.map((m, i) => {
                  const pct = total > 1 ? (i / (total - 1)) * 100 : 0;
                  const isActive = i === idx;
                  const isPast = i < idx;
                  return (
                    <div
                      key={i}
                      data-testid={`story-year-${i}`}
                      className="absolute -top-[6px] -translate-x-1/2 flex flex-col items-center pointer-events-none"
                      style={{ left: `${pct}%` }}
                    >
                      <span
                        className="block rounded-full transition-all"
                        style={{
                          width:  isActive ? 14 : 9,
                          height: isActive ? 14 : 9,
                          background: isActive ? "#1B6FE0" : (isPast ? "#0F2A6E" : "#CBD5E1"),
                          boxShadow: isActive ? "0 0 0 5px rgba(27,111,224,0.18)" : "none",
                          transitionDuration: "400ms",
                        }}
                      />
                      <span
                        className="mt-3 font-heading font-semibold tracking-tight whitespace-nowrap transition-all"
                        style={{
                          fontSize: isActive ? 14.5 : 12,
                          color: isActive ? "#0F2A6E" : (isPast ? "rgba(15,42,110,0.75)" : "rgba(15,42,110,0.40)"),
                          transitionDuration: "400ms",
                          letterSpacing: "-0.005em",
                        }}
                      >
                        {m.year}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MOBILE: vertical stacked panels ============ */}
      {!isDesktop && (
        <div className="px-4 py-14">
          <div
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold mb-3"
            style={{ color: "#1B6FE0" }}
          >
            <span className="w-8 h-px" style={{ background: "#1B6FE0" }} />
            {payload.eyebrow}
          </div>
          <h2
            className="font-heading font-bold tracking-tight text-3xl sm:text-4xl leading-[1.05] mb-3"
            style={{ letterSpacing: "-0.028em", color: "#0F2A6E" }}
            data-testid="story-timeline-heading"
          >
            {payload.heading}
          </h2>
          {payload.sub && (
            <p className="text-[14.5px] leading-relaxed font-light mb-10" style={{ color: "#1C2B3A" }}>
              {payload.sub}
            </p>
          )}

          <ol className="relative border-l-2 space-y-10" style={{ borderColor: "rgba(15,42,110,0.12)" }}>
            {milestones.map((m, i) => (
              <li key={i} className="relative pl-6" data-testid={`story-panel-${i}`}>
                <span
                  className="absolute -left-[9px] top-1 w-4 h-4 rounded-full"
                  style={{ background: "#1B6FE0", boxShadow: "0 0 0 4px rgba(27,111,224,0.18)" }}
                />
                <div className="font-heading font-bold text-3xl" style={{ color: "#0F2A6E", letterSpacing: "-0.02em" }}>
                  {m.year}
                </div>
                <h3
                  className="mt-2 font-heading font-bold text-xl tracking-tight"
                  style={{ color: "#0F2A6E", letterSpacing: "-0.02em" }}
                  data-testid={i === 0 ? "story-active-title" : `story-title-${i}`}
                >
                  {m.title}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed font-light" style={{ color: "#1C2B3A" }}>
                  {m.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
